import momentTz from 'moment-timezone';
import React, {Fragment} from 'react';
import {FormattedMessage} from 'react-intl';

import {groupByKey, unreachable} from '../utils';
import {cmpByKey} from '../utils/cmp';
import * as iter from '../utils/iter';

import {encodeGetParams} from './fetch';

const MONTH_IN_MS = 30 * 24 * 3600 * 1000;

/**
 * @typedef {{
 *   id: string,
 *   dates: Array<{
 *     date_from: string,
 *     time_from?: string,
 *     date_to?: string,
 *     time_to?: string,
 *     weekdays?: {
 *       mo: boolean,
 *       di: boolean,
 *       mi: boolean,
 *       do: boolean,
 *       fr: boolean,
 *       sa: boolean,
 *       so: boolean,
 *     },
 *   }>,
 *   categories: Array<string>,
 *   title: string,
 *   description: string,
 *   image?: {
 *     url: string,
 *     description?: string,
 *   },
 * }} RawEvent
 */

/**
 * @typedef {{
 *   date: Date,
 *   hasTime: boolean,
 * }} DateTime
 */

/**
 * @typedef {{
 *   id: string,
 *   category: string,
 *   title: string,
 *   description: {de: {html: string}},
 *   dates: Array<{
 *     from: DateTime,
 *     to: DateTime,
 *     weekdays: null | Array<[number, number]>,
 *   }>,
 *   image?: {
 *     url: string,
 *     description?: string,
 *   },
 * }} Event
 */

/**
 * @param {Date} date
 * @returns {string} ...
 */
function encodeEventDate(date) {
	return momentTz(date).tz('Europe/Berlin').format('YYYY-MM-DD');
}

/**
 * @param {string} date
 * @param {string | null | undefined} time
 * @returns {DateTime} ...
 */
function decodeDateTime(date, time) {
	const dateParts = date?.split('-');
	const timeParts = time?.split(':');

	const dateRes = momentTz.tz(
		[
			parseInt(dateParts[0], 10),
			parseInt(dateParts[1], 10) - 1,
			parseInt(dateParts[2], 10),
			timeParts ? parseInt(timeParts[0], 10) : 0,
			timeParts ? parseInt(timeParts[1], 10) : 0,
			timeParts ? parseInt(timeParts[2], 10) : 0,
		],
		'Europe/Berlin',
	)
		.toDate();
	return {
		date: dateRes,
		hasTime: !!time,
	};
}

/**
 * @param {DateTime} a
 * @param {DateTime} b
 * @returns {boolean} ...
 */
export function isDateEq(a, b) {
	return (
		a.date.getUTCFullYear() === b.date.getUTCFullYear() &&
		a.date.getUTCMonth() === b.date.getUTCMonth() &&
		a.date.getUTCDate() === b.date.getUTCDate()
	);
}

/**
 * @param {string} baseUrl base url for the events api endpoint
 * @returns {string} built url
 */
export function buildEventSourceUrl(baseUrl) {
	const now = new Date();
	const params = encodeGetParams({
		from: encodeEventDate(now),
		to: encodeEventDate(new Date(now.getTime() + 12 * MONTH_IN_MS)),
		tag: '287',
	});
	return `${baseUrl}${params}`;
}

/**
 * @param {{
 *   mo: boolean,
 *   di: boolean,
 *   mi: boolean,
 *   do: boolean,
 *   fr: boolean,
 *   sa: boolean,
 *   so: boolean,
 * }} weekdays
 * @returns {Array<[number, number]> | null} ...
 */
function calcWeekdayBounds(weekdays) {
	/** @type {Array<[number, number]>} */
	const res = [];
	/** @type {[number, number] | null} */
	let last = null;

	iter.from(function*() {
		yield !!weekdays.mo;
		yield !!weekdays.di;
		yield !!weekdays.mi;
		yield !!weekdays.do;
		yield !!weekdays.fr;
		yield !!weekdays.sa;
		yield !!weekdays.so;
	}())
		.asIndexedPairs()
		.forEach(([i, val]) => {
			if (val) {
				if (last !== null && last[1] === i - 1) {
					last[1] = i;
				} else {
					/** @type {[number, number]} */
					const newBound = [i, i];
					res.push(newBound);
					last = newBound;
				}
			}
		});

	if (res.length === 0) {
		return null;
	}
	return res;
}

/**
 * @param {number} index
 * @returns {React.ReactNode} ...
 */
// eslint-disable-next-line consistent-return
export function renderWeekdayIndex(index) {
	switch(index) {
		case 0:
			return (<FormattedMessage id="date.mondayShort" />);

		case 1:
			return (<FormattedMessage id="date.tuesdayShort" />);

		case 2:
			return (<FormattedMessage id="date.wednesdayShort" />);

		case 3:
			return (<FormattedMessage id="date.thursdayShort" />);

		case 4:
			return (<FormattedMessage id="date.fridayShort" />);

		case 5:
			return (<FormattedMessage id="date.saturdayShort" />);

		case 6:
			return (<FormattedMessage id="date.sundayShort" />);

		default:
			unreachable();
	}
}

/**
 * @param {Array<[number, number]> | null} bounds
 * @returns {React.ReactElement | null} ...
 */
export function renderWeekdayBounds(bounds) {
	if (bounds === null) {
		return null;
	}

	const it = iter.join(
		', ',
		iter.from(bounds).flatMap(([low, high]) => {
			function* gen() {
				yield renderWeekdayIndex(low);
				if (low !== high) {
					yield ' - ';
					yield renderWeekdayIndex(high);
				}
			}
			return iter.from(gen());
		})
	);

	return React.createElement(
		Fragment,
		null,
		// works only with arrays in loose mode
		...it.toArray(),
	);
}

/**
 * @param {Array<RawEvent>} rawEvents
 * @returns {Array<Array<Event>>} ...
 */
export function processEventData(rawEvents) {
	const events = iter.from(rawEvents)
		.map(({id, dates, categories, title, description, image}) => {
			const dates2 = dates.map((date) => {
				const weekdays = date.weekdays ? calcWeekdayBounds(date.weekdays) : null;
				return {
					from: decodeDateTime(date.date_from, date.time_from),
					to: decodeDateTime(date.date_to || date.date_from, date.time_to),
					weekdays,
				};
			});

			/** @type {Event} */
			const res = {
				id,
				category: categories && categories[0] ? categories[0] : 'Event',
				title,
				description: {
					de: {
						html: description,
					}
				},
				dates: dates2,
				image,
			};
			return res;
		});

	const groupedEvents = groupByKey(events, (e) => e.category)
		.map(([_, events2]) => {
			events2.sort(cmpByKey((e) => e.dates[0]?.from.date));
			return events2;
		})
		.toArray();

	// TODO: sort by category priority
	groupedEvents.sort(cmpByKey((e) => e[0].category));

	return groupedEvents;
}

/**
 * @param {any} input
 * @returns {Array<Array<Event>>} ...
 */
export function processSerializedEvents(input) {
	return input.map(
		(group) => group.map(
			(event) => ({
				...event,
				dates: event.dates.map(
					(date) => ({
						...date,
						from: {
							...date.from,
							date: new Date(date.from.date)
						},
						to: {
							...date.to,
							date: new Date(date.to.date),
						},
					})
				),
			}),
		),
	);
}
